
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Profile } from "./types";
import { useNotifications } from "@/hooks/useNotifications";

export const useProfileUpdate = () => {
  const queryClient = useQueryClient();
  const { success, error: showError, info } = useNotifications();

  return useMutation({
    mutationFn: async (updates: Partial<Profile>): Promise<Profile> => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("No authenticated user");
      }

      console.log("Actualizando perfil con:", updates);
      
      // Si se está actualizando el email (username), actualizar también en auth
      if (updates.username && updates.username !== user.email) {
        console.log("Actualizando email de autenticación:", updates.username);
        
        const { error: authError } = await supabase.auth.updateUser({
          email: updates.username
        });

        if (authError) {
          console.error("Error updating auth email:", authError);
          throw authError;
        }

        // Mostrar notificación especial para cambio de email
        info("Email actualizado", {
          description: "Se ha enviado un email de confirmación a tu nueva dirección. IMPORTANTE: Debes confirmar el nuevo email antes de realizar operaciones como gestionar clases.",
          duration: 8000
        });
      }
      
      const { data, error } = await supabase
        .from("profiles")
        .update(updates)
        .eq("id", user.id)
        .select()
        .single();

      if (error) {
        console.error("Error updating profile:", error);
        throw error;
      }

      console.log("Perfil actualizado exitosamente:", data);
      return data as Profile;
    },
    onSuccess: (data, variables) => {
      // Actualizar la caché directamente con los nuevos datos
      queryClient.setQueryData(["profile"], data);
      
      // También invalidar la consulta para forzar una nueva carga si es necesario
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      
      // Solo mostrar notificación de éxito si no se cambió el email (ya se mostró una específica)
      if (!variables.username) {
        success("Perfil actualizado", {
          description: "Los cambios han sido guardados correctamente."
        });
      }
    },
    onError: (error: any) => {
      console.error("Error updating profile:", error);
      
      // Manejar errores específicos de email
      if (error.message?.includes("email") || error.message?.includes("Email")) {
        showError(error.message?.includes("already") 
          ? "Esta dirección de email ya está en uso por otra cuenta."
          : "No se pudo actualizar el email. Verifica que sea una dirección válida.");
      } else {
        showError("No se pudo actualizar el perfil. Por favor, intenta nuevamente.");
      }
    },
  });
};
